import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from "prop-types";
import Box from '@mui/material/Box';

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import { center, bbox, bboxPolygon } from "@turf/turf";

import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';
import deviceSleeping from "assets/images/device_sleeping.png";
import deviceEcoMode from "assets/images/eco_mode.png";
import { setGroupFeatures, setDeviceFeatures } from 'utils/map';
import { useSoftUIController } from 'hooks/context';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function Map({ selected_group, parent_group = {}, allow_devices = false, settings, padding }) {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const resizeObserver = useRef(null);

    const [controller] = useSoftUIController();
    const { selectedOrg, userPermissions } = controller;
    const navigate = useNavigate();

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
    });

    const handleDeviceClick = (e) => {
        e.preventDefault();
        popup.remove();
        navigate("/devices/" + e.features[0].properties.uuid);
    }

    const handleMouseEnter = (e) => {
        if (!map.current) return;
        map.current.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();
        let description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
        if (showAdvancedDeviceLevel) {
            description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
        }

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
    }

    const handleMouseLeave = () => {
        if (!map.current) return;
        map.current.getCanvas().style.cursor = '';
        popup.remove();
    }

    useEffect(() => {
        if (map.current || !mapContainer.current) return;

        // Trackers to ensure fitBounds only happens when the map is ready AND visible
        let isMapLoaded = false;
        let hasFittedBounds = false;

        const bounds = bbox({
            "type": "FeatureCollection",
            "features": selected_group.geojson
        });

        const polygon = bboxPolygon(bounds);

        // Helper function to attempt fitting the bounds
        const attemptFitBounds = (duration = 0) => {
            if (!mapContainer.current || mapContainer.current.offsetWidth === 0) return; // Still hidden
            if (!isMapLoaded) return; // Map engine not fully booted yet
            if (hasFittedBounds) return; // We already successfully zoomed in

            map.current.fitBounds(polygon.bbox, { linear: true, padding: padding, duration: duration });
            hasFittedBounds = true;
        };

        // 1. Initialize Map
        map.current = new mapboxgl.Map({
            ...settings,
            container: mapContainer.current,
            interactive: settings?.interactive || false,
            center: center({
                "type": "FeatureCollection",
                "features": selected_group.geojson
            }).geometry.coordinates
        });

        // 2. Setup ResizeObserver for when tabs change from display:none to display:block
        resizeObserver.current = new ResizeObserver(() => {
            if (mapContainer.current && mapContainer.current.offsetWidth > 0 && map.current) {
                map.current.resize(); // Fix the distorted canvas
                attemptFitBounds(0);  // Use 0 duration so it snaps instantly when revealed
            }
        });
        resizeObserver.current.observe(mapContainer.current);

        // 3. Load Images and Wait for Map Load
        map.current.on('load', async () => {
            isMapLoaded = true; // Mark as ready for bounds fitting
            
            if (!map.current) return;

            const icons = [
                { id: 'cameraIconOnline', img: cameraOnline },
                { id: 'cameraIconWarning', img: cameraWarning },
                { id: 'cameraIconOffline', img: cameraOffline },
                { id: 'deviceIconSleeping', img: deviceSleeping },
                { id: 'deviceIconEcoMode', img: deviceEcoMode }
            ];

            await Promise.all(icons.map(icon => {
                return new Promise((resolve) => {
                    if (map.current.hasImage(icon.id)) {
                        resolve();
                    } else {
                        map.current.loadImage(icon.img, (err, res) => {
                            if (!err && res && !map.current.hasImage(icon.id)) {
                                map.current.addImage(icon.id, res);
                            }
                            resolve();
                        });
                    }
                });
            }));

            if (!map.current) return;

            // Add Selected Group Layer
            map.current.addSource('groups-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": setGroupFeatures(selected_group, showAdvancedDeviceLevel)
                }
            });

            if (typeof selected_group.parent_id != 'undefined') {
                map.current.addLayer({
                    id: 'groups-layer',
                    type: 'line',
                    source: 'groups-source',
                    paint: {
                        "line-color": ['get', 'feature_colour'],
                        "line-width": 4,
                    }
                });
            } else {
                map.current.addLayer({
                    id: 'groups-layer',
                    type: 'fill',
                    source: 'groups-source',
                    paint: {
                        'fill-color': ['get', 'feature_colour'],
                        'fill-opacity': 0.2,
                    }
                }).addLayer({
                    id: 'groups-layer-liner',
                    type: 'line',
                    source: 'groups-source',
                    paint: {
                        "line-color": ['get', 'feature_colour'],
                        "line-width": 2
                    }
                });
            }

            // Add Parent Group Layer
            if (typeof parent_group.group_id != 'undefined') {
                map.current.addSource('groups-parent-source', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": setGroupFeatures(parent_group, showAdvancedDeviceLevel, true)
                    }
                });

                map.current.addLayer({
                    id: 'groups-parent-layer',
                    type: 'fill',
                    source: 'groups-parent-source',
                    paint: {
                        "fill-color": "black",
                        "fill-opacity": 0.1
                    }
                }).addLayer({
                    id: 'groups-parent-layer-liner',
                    type: 'line',
                    source: 'groups-parent-source',
                    paint: {
                        "line-color": "black",
                        "line-width": 2,
                        "line-opacity": 0.2
                    }
                });
            }

            // Add Devices
            if (allow_devices) {
                map.current.addSource('devices-img-source', {
                    type: 'geojson',
                    data: {
                        type: "FeatureCollection",
                        features: selected_group.devices.filter(d => (d.hardware_type === "bk-camera" || d.connected_state == 'sleeping' || d.connected_state == 'eco') && d.active)
                            .map(d => setDeviceFeatures(d, showAdvancedDeviceLevel)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
                    }
                });

                map.current.addLayer({
                    id: 'devices-img-layer',
                    type: 'symbol',
                    source: 'devices-img-source',
                    layout: {
                        'icon-image': ['get', 'icon'],
                        'icon-size': 0.042,
                        'icon-allow-overlap': true
                    }
                });

                map.current.addSource('devices-source', {
                    type: 'geojson',
                    data: {
                        type: "FeatureCollection",
                        features: selected_group.devices.filter(d => d.hardware_type !== "bk-camera" && d.connected_state != 'sleeping' && d.connected_state != 'eco' && d.active)
                            .map(d => setDeviceFeatures(d, showAdvancedDeviceLevel)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
                    }
                });

                map.current.addLayer({
                    id: 'devices-layer',
                    type: 'circle',
                    source: 'devices-source',
                    paint: {
                        'circle-color': ['get', 'feature_colour'],
                        'circle-radius': 9,
                        'circle-stroke-width': 3,
                        'circle-stroke-color': '#ffffff'
                    }
                });

                map.current.on('click', 'devices-img-layer', handleDeviceClick);
                map.current.on('mouseenter', 'devices-img-layer', handleMouseEnter);
                map.current.on('mouseleave', 'devices-img-layer', handleMouseLeave);
                
                map.current.on('click', 'devices-layer', handleDeviceClick);
                map.current.on('mouseenter', 'devices-layer', handleMouseEnter);
                map.current.on('mouseleave', 'devices-layer', handleMouseLeave);
            }

            // Attempt to fit bounds if this is the active (visible) tab
            // Use 200ms duration for a nice intro animation on the active tab
            attemptFitBounds(200); 
        });

        // 4. Cleanup Function
        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
            }
            
            try {
                popup.remove();
                if (map.current) {
                    map.current.remove(); 
                    map.current = null;
                }
            } catch (e) {
                console.error("Mapbox unmount error:", e);
            }
        };
    }, [selectedOrg]);

    return (
        <Box component="div" ref={mapContainer} sx={{ height: '100%', width: '100%' }} />
    );
}

Map.propTypes = {
    selected_group: PropTypes.object.isRequired,
    parent_group: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    allow_devices: PropTypes.bool,
    settings: PropTypes.object,
    padding: PropTypes.number
};

export default Map;